/**
 * Backend API Service
 * Handles all communication with the new backend session management APIs
 */

import { apiConfig } from '../config/credentials'
import { PersistentPreferences, SessionPreferences } from '../types/preferences'
import { ConversationHistoryResponse } from '../types/conversation'
import { BackendSession } from '../types/session'
import { BackendMealPlanResponse } from '../types/mealPlan'
import { BaseApiService } from './BaseApiService'
import { clientLogger } from '../utils/clientLogger'

class BackendApiService extends BaseApiService {
  constructor() {
    super({
      baseUrl: apiConfig.baseUrl,
      defaultTimeout: 15000,
      defaultRetries: 3,
      enableRetry: true
    })
  }

  /**
   * Create a new meal planning session (LLM conversation in existing meal_plan_session)
   */
  async createSession(
    userId: string,
    mealSessionId: string,
    persistentPreferences: PersistentPreferences,
    sessionPreferences: SessionPreferences
  ): Promise<BackendSession> {
    clientLogger.debug({ userId, mealSessionId }, 'Creating session')

    const session = await this.post<BackendSession>('/api/sessions', {
      userId,
      mealSessionId,
      persistentPreferences,
      sessionPreferences
    })

    clientLogger.debug({ sessionId: session.sessionId }, 'Session created')
    return session
  }

  /**
   * Get an existing session
   */
  async getSession(sessionId: string, userId: string): Promise<BackendSession | null> {
    clientLogger.debug({ sessionId }, 'Getting session')

    try {
      const session = await this.get<BackendSession>(`/api/sessions/${sessionId}?userId=${userId}`)
      clientLogger.debug({ sessionId }, 'Session retrieved')
      return session
    } catch (error: any) {
      if (error.message.includes('404')) {
        clientLogger.debug({ sessionId }, 'Session not found')
        return null
      }
      throw error
    }
  }

  /**
   * Update session preferences
   */
  async updateSession(
    sessionId: string,
    userId: string,
    updates: {
      persistentPreferences?: PersistentPreferences
      sessionPreferences?: SessionPreferences
    }
  ): Promise<BackendSession> {
    clientLogger.debug({ sessionId }, 'Updating session')

    const response = await fetch(`${this.baseUrl}/api/sessions/${sessionId}`, {
      method: 'PUT',
      headers: await this.getAuthHeaders(),
      body: JSON.stringify({
        userId,
        ...updates
      })
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to update session: ${response.status} ${response.statusText} - ${errorText}`)
    }

    const session = await response.json()
    clientLogger.debug({ sessionId }, 'Session updated')
    return session
  }

  /**
   * Send a message to Claude via backend
   */
  async sendMessage(
    sessionId: string,
    userId: string,
    message: string,
    metadata?: { recipeToReplace?: string; clearHistory?: boolean }
  ): Promise<BackendMealPlanResponse> {
    clientLogger.debug({ sessionId, messageLength: message.length, metadata }, 'Sending message')

    const response = await fetch(`${this.baseUrl}/api/sessions/${sessionId}/messages`, {
      method: 'POST',
      headers: await this.getAuthHeaders(),
      body: JSON.stringify({
        userId,
        message,
        ...metadata
      })
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to send message: ${response.status} ${response.statusText} - ${errorText}`)
    }

    const result = await response.json()
    clientLogger.debug({ sessionId: result.sessionId, responseLength: result.response?.length ?? 0, recipesCount: result.recipes?.length || 0 }, 'Message response received')
    return result
  }

  /**
   * Get conversation history
   */
  async getConversationHistory(
    sessionId: string,
    userId: string
  ): Promise<ConversationHistoryResponse> {
    clientLogger.debug({ sessionId }, 'Getting conversation history')

    const response = await fetch(`${this.baseUrl}/api/sessions/${sessionId}/conversation?userId=${userId}`, {
      method: 'GET',
      headers: await this.getAuthHeaders()
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to get conversation: ${response.status} ${response.statusText} - ${errorText}`)
    }

    const conversation = await response.json()
    clientLogger.debug({ sessionId: conversation.sessionId, messageCount: conversation.messageCount }, 'Conversation retrieved')
    return conversation
  }

  /**
   * Delete a session
   */
  async deleteSession(sessionId: string, userId: string): Promise<void> {
    clientLogger.debug({ sessionId }, 'Deleting session')

    const response = await fetch(`${this.baseUrl}/api/sessions/${sessionId}?userId=${userId}`, {
      method: 'DELETE'
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to delete session: ${response.status} ${response.statusText} - ${errorText}`)
    }

    clientLogger.debug({ sessionId }, 'Session deleted')
  }

  /**
   * Test backend connectivity
   */
  async testConnectivity(): Promise<boolean> {
    try {
      clientLogger.debug({ url: `${this.baseUrl}/health` }, 'Testing connectivity')
      const response = await fetch(`${this.baseUrl}/health`)
      const isConnected = response.ok
      clientLogger.info({ isConnected }, `Connectivity test: ${isConnected ? 'SUCCESS' : 'FAILED'}`)
      return isConnected
    } catch (error) {
      clientLogger.error({ err: error instanceof Error ? error : new Error(String(error)) }, 'Connectivity test error')
      return false
    }
  }

  // User Preference Management Methods

  /**
   * Save persistent preferences for authenticated users
   */
  async savePersistentPreferences(
    userId: string,
    preferences: PersistentPreferences
  ): Promise<{ success: boolean; data: any }> {
    clientLogger.debug({ userId }, 'Saving persistent preferences for user')

    const response = await fetch(`${this.baseUrl}/api/users/${userId}/preferences/persistent`, {
      method: 'POST',
      headers: await this.getAuthHeaders(),
      body: JSON.stringify({ preferences })
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to save persistent preferences: ${response.status} ${response.statusText} - ${errorText}`)
    }

    const result = await response.json()
    clientLogger.debug({}, 'Persistent preferences saved successfully')
    return result
  }

  /**
   * Load persistent preferences for authenticated users
   */
  async loadPersistentPreferences(userId: string): Promise<{
    persistent: PersistentPreferences
    setup_completed: boolean
    last_updated: string
  } | null> {
    clientLogger.debug({ userId }, 'Loading persistent preferences for user')

    const response = await fetch(`${this.baseUrl}/api/users/${userId}/preferences/persistent`, {
      method: 'GET',
      headers: await this.getAuthHeaders()
    })

    if (response.status === 404) {
      clientLogger.debug({ userId }, 'No persistent preferences found for user')
      return null
    }

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to load persistent preferences: ${response.status} ${response.statusText} - ${errorText}`)
    }

    const result = await response.json()
    clientLogger.debug({}, 'Persistent preferences loaded successfully')
    return result.data
  }

  /**
   * Save session preferences for authenticated users
   */
  async saveSessionPreferences(
    userId: string,
    preferences: SessionPreferences
  ): Promise<{ success: boolean; data: any }> {
    clientLogger.debug({ userId }, 'Saving session preferences for user')

    const response = await fetch(`${this.baseUrl}/api/users/${userId}/preferences/session`, {
      method: 'POST',
      headers: await this.getAuthHeaders(),
      body: JSON.stringify({ preferences })
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to save session preferences: ${response.status} ${response.statusText} - ${errorText}`)
    }

    const result = await response.json()
    clientLogger.debug({}, 'Session preferences saved successfully')
    return result
  }

  /**
   * Load session preferences for authenticated users
   */
  async loadSessionPreferences(userId: string): Promise<SessionPreferences | null> {
    clientLogger.debug({ userId }, 'Loading session preferences for user')

    const response = await fetch(`${this.baseUrl}/api/users/${userId}/preferences/session`, {
      method: 'GET',
      headers: await this.getAuthHeaders()
    })

    if (response.status === 404) {
      clientLogger.debug({ userId }, 'No session preferences found for user')
      return null
    }

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to load session preferences: ${response.status} ${response.statusText} - ${errorText}`)
    }

    const result = await response.json()
    clientLogger.debug({}, 'Session preferences loaded successfully')
    return result.data
  }

  /**
   * Clear session preferences for authenticated users
   */
  async clearSessionPreferences(userId: string): Promise<{ success: boolean; data: any }> {
    clientLogger.debug({ userId }, 'Clearing session preferences for user')

    const response = await fetch(`${this.baseUrl}/api/users/${userId}/preferences/session`, {
      method: 'DELETE',
      headers: await this.getAuthHeaders()
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to clear session preferences: ${response.status} ${response.statusText} - ${errorText}`)
    }

    const result = await response.json()
    clientLogger.debug({}, 'Session preferences cleared successfully')
    return result
  }

  /**
   * Save preferences for guest sessions
   */
  async saveGuestPreferences(
    guestSessionId: string,
    persistentPreferences?: PersistentPreferences,
    sessionPreferences?: SessionPreferences
  ): Promise<{ success: boolean; data: any }> {
    clientLogger.debug({ guestSessionId }, 'Saving guest preferences for session')

    const response = await fetch(`${this.baseUrl}/api/guests/${guestSessionId}/preferences`, {
      method: 'POST',
      headers: await this.getAuthHeaders(),
      body: JSON.stringify({
        persistentPreferences,
        sessionPreferences
      })
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to save guest preferences: ${response.status} ${response.statusText} - ${errorText}`)
    }

    const result = await response.json()
    clientLogger.debug({}, 'Guest preferences saved successfully')
    return result
  }

  /**
   * Load preferences for guest sessions
   */
  async loadGuestPreferences(guestSessionId: string): Promise<{
    persistent: PersistentPreferences | null
    session: SessionPreferences | null
    created_at: string
  } | null> {
    clientLogger.debug({ guestSessionId }, 'Loading guest preferences for session')

    const response = await fetch(`${this.baseUrl}/api/guests/${guestSessionId}/preferences`, {
      method: 'GET',
      headers: await this.getAuthHeaders()
    })

    if (response.status === 404) {
      clientLogger.debug({ guestSessionId }, 'No guest preferences found for session')
      return null
    }

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to load guest preferences: ${response.status} ${response.statusText} - ${errorText}`)
    }

    const result = await response.json()
    clientLogger.debug({}, 'Guest preferences loaded successfully')
    return result.data
  }

  /**
   * Migrate guest preferences to authenticated user account
   */
  async migrateGuestPreferences(
    userId: string,
    guestSessionId: string
  ): Promise<{ success: boolean; migrated: boolean }> {
    clientLogger.debug({ guestSessionId, userId }, 'Migrating guest preferences to user')

    const response = await fetch(`${this.baseUrl}/api/users/${userId}/preferences/migrate/${guestSessionId}`, {
      method: 'POST',
      headers: await this.getAuthHeaders()
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to migrate guest preferences: ${response.status} ${response.statusText} - ${errorText}`)
    }

    const result = await response.json()
    clientLogger.info({ migrated: result.migrated }, `Guest preferences migration result: ${result.migrated ? 'SUCCESS' : 'NO_DATA'}`)
    return result
  }

  // Recipe Management Methods

  /**
   * Create or get meal plan session for recipe storage
   */
  async createOrGetMealSession(
    userId: string | null,
    guestSessionId: string | null,
    sessionPreferences: any,
    persistentPreferences: any
  ): Promise<{ success: boolean; session: any }> {
    clientLogger.debug({ userId: userId || guestSessionId }, 'Creating/getting meal session for user')

    const response = await fetch(`${this.baseUrl}/api/meal-sessions`, {
      method: 'POST',
      headers: await this.getAuthHeaders(),
      body: JSON.stringify({
        userId,
        guestSessionId,
        sessionPreferences,
        persistentPreferences
      })
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to create/get meal session: ${response.status} ${response.statusText} - ${errorText}`)
    }

    const result = await response.json()
    clientLogger.debug({ sessionId: result.session.id }, 'Meal session created/retrieved')
    return result
  }

  /**
   * Save recipe to meal session
   */
  async saveRecipeToSession(
    sessionId: string,
    recipeData: any,
    mealType?: string
  ): Promise<{ success: boolean; data: any }> {
    clientLogger.debug({ sessionId, recipeName: recipeData.name }, 'Saving recipe to session')

    const response = await fetch(`${this.baseUrl}/api/meal-sessions/${sessionId}/recipes`, {
      method: 'POST',
      headers: await this.getAuthHeaders(),
      body: JSON.stringify({
        recipeData,
        mealType
      })
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to save recipe to session: ${response.status} ${response.statusText} - ${errorText}`)
    }

    const result = await response.json()
    clientLogger.debug({}, 'Recipe saved successfully')
    return result
  }

  /**
   * Get all recipes for a meal session
   */
  async getSessionRecipes(sessionId: string): Promise<any[]> {
    clientLogger.debug({ sessionId }, 'Getting recipes for session')

    const response = await fetch(`${this.baseUrl}/api/meal-sessions/${sessionId}/recipes`, {
      method: 'GET',
      headers: await this.getAuthHeaders()
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to get session recipes: ${response.status} ${response.statusText} - ${errorText}`)
    }

    const result = await response.json()
    clientLogger.debug({ count: result.count }, 'Retrieved recipes')
    return result.recipes
  }

  /**
   * Delete a single recipe from a meal session by name
   */
  async deleteSessionRecipe(sessionId: string, recipeName: string): Promise<{ success: boolean }> {
    clientLogger.debug({ sessionId, recipeName }, 'Deleting recipe from session')

    const encodedName = encodeURIComponent(recipeName)
    const response = await fetch(`${this.baseUrl}/api/meal-sessions/${sessionId}/recipes/${encodedName}`, {
      method: 'DELETE',
      headers: await this.getAuthHeaders()
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to delete recipe from session: ${response.status} ${response.statusText} - ${errorText}`)
    }

    const result = await response.json()
    clientLogger.debug({ recipeName }, 'Recipe deleted successfully')
    return result
  }

  /**
   * Add a single extra recipe slot for a given meal type.
   * The backend saves a 'fetching' placeholder immediately and generates the
   * full recipe details in the background, avoiding the chat/tag-parsing flow.
   */
  async addRecipeSlot(
    sessionId: string,
    mealType: 'breakfast' | 'lunch' | 'dinner' | 'snacks'
  ): Promise<{ success: boolean; recipeName: string; mealType: string }> {
    clientLogger.debug({ sessionId, mealType }, 'Adding recipe slot')

    const response = await fetch(`${this.baseUrl}/api/meal-sessions/${sessionId}/recipes/add-slot`, {
      method: 'POST',
      headers: await this.getAuthHeaders(),
      body: JSON.stringify({ mealType })
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to add recipe slot: ${response.status} ${response.statusText} - ${errorText}`)
    }

    const result = await response.json()
    clientLogger.debug({ recipeName: result.recipeName, mealType }, 'Recipe slot added, fetch in progress')
    return result
  }

  /**
   * Clear all recipes for a meal session
   */
  async clearSessionRecipes(sessionId: string): Promise<{ success: boolean }> {
    clientLogger.debug({ sessionId }, 'Clearing recipes for session')

    const response = await fetch(`${this.baseUrl}/api/meal-sessions/${sessionId}/recipes`, {
      method: 'DELETE',
      headers: await this.getAuthHeaders()
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to clear session recipes: ${response.status} ${response.statusText} - ${errorText}`)
    }

    const result = await response.json()
    clientLogger.debug({}, 'Recipes cleared successfully')
    return result
  }

  /**
   * Update meal plan session status
   */
  async updateMealSessionStatus(
    sessionId: string,
    status: 'active' | 'shopping' | 'scheduling' | 'complete'
  ): Promise<{ success: boolean; session: any }> {
    clientLogger.debug({ sessionId, status }, 'Updating meal session status')

    const response = await fetch(`${this.baseUrl}/api/meal-sessions/${sessionId}/status`, {
      method: 'PUT',
      headers: await this.getAuthHeaders(),
      body: JSON.stringify({ status })
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to update meal session status: ${response.status} ${response.statusText} - ${errorText}`)
    }

    const result = await response.json()
    clientLogger.debug({ sessionId: result.session.id }, 'Meal session status updated')
    return result
  }

  /**
   * Promote recipes from session_recipes to recipes table
   * This copies recipes from the temporary session_recipes table to the permanent recipes table
   * and deletes the session_recipes entries after successful promotion
   */
  async promoteSessionRecipes(
    userId: string,
    sessionId: string
  ): Promise<{ success: boolean; promoted: number }> {
    clientLogger.debug({ sessionId }, 'Promoting recipes for session')

    const response = await fetch(`${this.baseUrl}/api/meal-sessions/${sessionId}/promote-recipes`, {
      method: 'POST',
      headers: await this.getAuthHeaders(),
      body: JSON.stringify({ userId })
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to promote recipes: ${response.status} ${response.statusText} - ${errorText}`)
    }

    const result = await response.json()
    clientLogger.debug({ promoted: result.promoted }, 'Promoted recipes')
    return result
  }

  /**
   * Get meal sessions by status for a user
   */
  async getMealSessionsByStatus(
    userId: string,
    statuses: string[]
  ): Promise<{ success: boolean; sessions: any[] }> {
    clientLogger.debug({ statuses }, 'Getting meal sessions with statuses')

    const statusParam = statuses.join(',')
    const response = await fetch(`${this.baseUrl}/api/users/${userId}/meal-sessions?status=${statusParam}`, {
      method: 'GET',
      headers: await this.getAuthHeaders()
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to get meal sessions: ${response.status} ${response.statusText} - ${errorText}`)
    }

    const result = await response.json()
    clientLogger.debug({ count: result.sessions.length }, 'Found meal sessions')
    return result
  }

  // Auth-related Database Operations

  async getGuestSession(guestToken: string): Promise<{ success: boolean; data: any }> {
    clientLogger.debug({}, 'Getting guest session')

    const response = await fetch(`${this.baseUrl}/api/guests/${guestToken}/session`, {
      method: 'GET',
      headers: await this.getAuthHeaders()
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to get guest session: ${response.status} ${response.statusText} - ${errorText}`)
    }

    const result = await response.json()
    return result
  }

  async createGuestSession(guestSession: any): Promise<{ success: boolean; data: any }> {
    clientLogger.debug({}, 'Creating guest session')

    const response = await fetch(`${this.baseUrl}/api/guests`, {
      method: 'POST',
      headers: await this.getAuthHeaders(),
      body: JSON.stringify({ guestSession })
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to create guest session: ${response.status} ${response.statusText} - ${errorText}`)
    }

    const result = await response.json()
    clientLogger.debug({}, 'Guest session created')
    return result
  }

  async deleteGuestSession(guestSessionId: string): Promise<{ success: boolean; message: string }> {
    clientLogger.debug({}, 'Deleting guest session')

    const response = await fetch(`${this.baseUrl}/api/guests/${guestSessionId}`, {
      method: 'DELETE',
      headers: await this.getAuthHeaders()
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to delete guest session: ${response.status} ${response.statusText} - ${errorText}`)
    }

    const result = await response.json()
    clientLogger.debug({}, 'Guest session deleted')
    return result
  }

  /**
   * Get user profile by custom user ID (user_profiles.user_id)
   * Use this for all operations after authentication
   */
  async getUserProfile(userId: string): Promise<{ success: boolean; data: any }> {
    clientLogger.debug({ userId }, 'Getting user profile by custom user ID')

    const response = await fetch(`${this.baseUrl}/api/users/${userId}/profile`, {
      method: 'GET',
      headers: await this.getAuthHeaders()
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to get user profile: ${response.status} ${response.statusText} - ${errorText}`)
    }

    const result = await response.json()
    return result
  }

  /**
   * Get user profile by Supabase auth user ID (auth.users.id)
   * Use this ONLY during authentication flow to fetch profile
   */
  async getUserProfileBySupabaseId(supabaseUserId: string): Promise<{ success: boolean; data: any }> {
    clientLogger.debug({ supabaseUserId }, 'Getting user profile by Supabase auth ID')

    const headers = await this.getAuthHeaders()
    clientLogger.debug({ url: `${this.baseUrl}/api/users/by-supabase-id/${supabaseUserId}/profile` }, 'Got auth headers, making fetch request')

    const response = await this.fetchWithTimeout(`${this.baseUrl}/api/users/by-supabase-id/${supabaseUserId}/profile`, {
      method: 'GET',
      headers
    })

    clientLogger.debug({ status: response.status }, 'Fetch completed')

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to get user profile by Supabase ID: ${response.status} ${response.statusText} - ${errorText}`)
    }

    const result = await response.json()
    clientLogger.debug({}, 'Profile retrieved successfully')
    return result
  }

  async createUserProfile(userId: string, profile: any): Promise<{ success: boolean; data: any }> {
    clientLogger.debug({ userId }, 'Creating user profile')

    const response = await fetch(`${this.baseUrl}/api/users/${userId}/profile`, {
      method: 'POST',
      headers: await this.getAuthHeaders(),
      body: JSON.stringify({ profile })
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to create user profile: ${response.status} ${response.statusText} - ${errorText}`)
    }

    const result = await response.json()
    clientLogger.debug({}, 'User profile created')
    return result
  }

  async updateUserProfile(userId: string, updates: any): Promise<{ success: boolean; data: any }> {
    clientLogger.debug({ userId }, 'Updating user profile')

    const response = await fetch(`${this.baseUrl}/api/users/${userId}/profile`, {
      method: 'PUT',
      headers: await this.getAuthHeaders(),
      body: JSON.stringify({ updates })
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to update user profile: ${response.status} ${response.statusText} - ${errorText}`)
    }

    const result = await response.json()
    clientLogger.debug({}, 'User profile updated')
    return result
  }

  /**
   * Promote guest user to authenticated or create new authenticated user
   * Used during OAuth login flow to:
   * 1. Promote guest user to authenticated (if guestUserId provided)
   * 2. Link multiple auth providers to same email
   * 3. Create new user profile for first-time OAuth users
   *
   * Requires valid Supabase JWT token (user must be authenticated)
   */
  async promoteUser(
    guestUserId: string | null,
    supabaseUserId: string,
    email: string,
    authProvider: string
  ): Promise<{ success: boolean; userId: string; wasGuest: boolean; linked: boolean; message: string }> {
    clientLogger.debug({ guestUserId, supabaseUserId, email, authProvider }, 'Promoting user')

    const response = await fetch(`${this.baseUrl}/api/users/promote`, {
      method: 'POST',
      headers: await this.getAuthHeaders(),
      body: JSON.stringify({
        guestUserId,
        supabaseUserId,
        email,
        authProvider
      })
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to promote user: ${response.status} ${response.statusText} - ${errorText}`)
    }

    const result = await response.json()
    clientLogger.info({ userId: result.userId }, 'User promoted successfully')
    return result
  }

  // Instacart Integration

  /**
   * Create Instacart shopping link via backend
   */
  async createInstacartLink(shoppingList: any): Promise<string> {
    clientLogger.debug({ name: shoppingList.name }, 'Creating Instacart link')

    const response = await fetch(`${this.baseUrl}/api/instacart/create-link`, {
      method: 'POST',
      headers: await this.getAuthHeaders(),
      body: JSON.stringify({ shoppingList })
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      const message = errorData.error || `Failed to create Instacart link: ${response.status} ${response.statusText}`
      const err = new Error(message) as Error & { retryable?: boolean }
      err.retryable = errorData.retryable === true
      throw err
    }

    const result = await response.json()
    clientLogger.debug({ url: result.url }, 'Instacart link created')
    return result.url
  }

  // ============================================================================
  // Subscription Management
  // ============================================================================

  /**
   * Get current user's subscription details
   * @returns Subscription details (tier, status, platform, dates)
   */
  async getSubscription(): Promise<any> {
    clientLogger.debug({}, 'Getting subscription')

    const response = await fetch(`${this.baseUrl}/api/subscriptions/me`, {
      method: 'GET',
      headers: await this.getAuthHeaders()
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to get subscription: ${response.status} ${response.statusText} - ${errorText}`)
    }

    const result = await response.json()
    clientLogger.debug({ tier: result.data.tier }, 'Subscription retrieved')
    return result.data
  }

  /**
   * Get usage statistics for current billing period
   * @returns Usage stats with limits and remaining quota
   */
  async getUsageStats(): Promise<any> {
    clientLogger.debug({}, 'Getting usage stats')

    const response = await fetch(`${this.baseUrl}/api/subscriptions/usage`, {
      method: 'GET',
      headers: await this.getAuthHeaders()
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to get usage stats: ${response.status} ${response.statusText} - ${errorText}`)
    }

    const result = await response.json()
    clientLogger.debug({}, 'Usage stats retrieved')
    return result.data
  }

  /**
   * Get features available to current user's tier
   * @returns Features list with availability flags
   */
  async getSubscriptionFeatures(): Promise<any> {
    clientLogger.debug({}, 'Getting subscription features')

    const response = await fetch(`${this.baseUrl}/api/subscriptions/features`, {
      method: 'GET',
      headers: await this.getAuthHeaders()
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to get subscription features: ${response.status} ${response.statusText} - ${errorText}`)
    }

    const result = await response.json()
    clientLogger.debug({ tier: result.data.tier }, 'Features retrieved for tier')
    return result.data
  }

  /**
   * Create Stripe Checkout session for initial subscription purchase
   * @param tier - Subscription tier (premium or pro)
   * @param interval - Billing interval (monthly or yearly)
   * @returns Checkout URL and session ID
   */
  async createStripeCheckout(tier: 'premium', interval: 'monthly' | 'yearly'): Promise<{ url: string; sessionId: string }> {
    clientLogger.debug({ tier, interval }, 'Creating Stripe Checkout session')

    const response = await fetch(`${this.baseUrl}/api/subscriptions/stripe/create-checkout`, {
      method: 'POST',
      headers: await this.getAuthHeaders(),
      body: JSON.stringify({ tier, interval })
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to create Stripe Checkout: ${response.status} ${response.statusText} - ${errorText}`)
    }

    const result = await response.json()
    clientLogger.debug({ sessionId: result.data.sessionId }, 'Stripe Checkout session created')
    return result.data
  }

  /**
   * Get Stripe Customer Portal URL for subscription management
   * Portal allows users to: update payment, view invoices, cancel subscription
   * @returns Customer Portal URL
   */
  async getStripePortalUrl(): Promise<{ url: string }> {
    clientLogger.debug({}, 'Getting Stripe Customer Portal URL')

    const response = await fetch(`${this.baseUrl}/api/subscriptions/stripe/portal-url`, {
      method: 'GET',
      headers: await this.getAuthHeaders()
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to get Stripe Portal URL: ${response.status} ${response.statusText} - ${errorText}`)
    }

    const result = await response.json()
    clientLogger.debug({}, 'Stripe Portal URL retrieved')
    return result.data
  }

  /**
   * Check if user can perform action within usage limits
   * @param action - Action type (create_meal_plan or global_chat_message)
   * @returns Whether action is allowed and remaining quota
   */
  async checkUsageLimit(action: 'create_meal_plan' | 'global_chat_message'): Promise<{ allowed: boolean; remaining: number | null }> {
    clientLogger.debug({ action }, 'Checking usage limit')

    // For now, just get usage stats and check locally
    const usage = await this.getUsageStats()

    if (action === 'create_meal_plan') {
      const limit = usage.limits.mealPlansPerMonth
      if (limit === null) {
        return { allowed: true, remaining: null } // unlimited
      }
      const remaining = limit - usage.usage.mealPlansThisMonth
      return {
        allowed: remaining > 0,
        remaining: Math.max(0, remaining)
      }
    } else if (action === 'global_chat_message') {
      const limit = usage.limits.globalChatPerWeek
      if (limit === null) {
        return { allowed: true, remaining: null } // unlimited
      }
      const remaining = limit - usage.usage.globalChatThisWeek
      return {
        allowed: remaining > 0,
        remaining: Math.max(0, remaining)
      }
    }

    return { allowed: true, remaining: null }
  }
}

export const backendApiService = new BackendApiService()
export default backendApiService